import { useEffect, useState } from 'react';
import { client } from '../api/client';
import { useAppStore } from '../store';
import { Sparkles, PenTool, BookOpen, Target, CheckCircle2, ArrowLeft, Loader2, AlertCircle } from 'lucide-react';

export default function AuthPage() {
  const { setActiveTab, setUser } = useAppStore();
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [inviteOnlySignup, setInviteOnlySignup] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const features = [
    { icon: Target, title: 'AI 智能纠错', desc: 'Gemini 驱动，精准识别 10 类语法错误' },
    { icon: PenTool, title: '主动练习', desc: '动手改正每个错误，记忆更深刻' },
    { icon: BookOpen, title: '个人错题集', desc: '自动收藏错误，随时复习巩固' },
  ];

  useEffect(() => {
    let cancelled = false;

    client.auth.getPublicAppConfig().then(({ data }) => {
      if (!cancelled) {
        setInviteOnlySignup(Boolean(data?.inviteOnlySignup));
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async () => {
    setError(null);

    if (!email.trim()) {
      setError('请输入邮箱地址。');
      return;
    }

    if (password.length < 8) {
      setError('密码至少需要 8 位。');
      return;
    }

    if (mode === 'signup' && password !== confirmPassword) {
      setError('两次输入的密码不一致。');
      return;
    }

    if (mode === 'signup' && inviteOnlySignup && !inviteCode.trim()) {
      setError('请输入邀请码。');
      return;
    }

    setIsSubmitting(true);
    try {
      const response =
        mode === 'signin'
          ? await client.auth.signIn(email, password)
          : await client.auth.signUp(email, password, name.trim() || undefined, inviteCode.trim() || undefined);

      setUser(response.data.user);
      setActiveTab('home');
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-emerald-100/60 via-emerald-50/30 to-transparent rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-amber-50/50 via-orange-50/20 to-transparent rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-white/40 to-transparent rounded-full" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-6">
        <button
          onClick={() => setActiveTab('home')}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-primary text-sm font-medium transition-all hover:-translate-x-0.5"
        >
          <ArrowLeft size={16} />
          返回首页
        </button>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-8 lg:py-16">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <div className="flex-1 max-w-lg text-center lg:text-left animate-fade-in-up">
            <div className="flex items-center gap-3 justify-center lg:justify-start mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                <Sparkles size={24} className="text-white" />
              </div>
              <span className="font-serif text-4xl text-primary tracking-tight">Refine 写作精修</span>
            </div>

            <h1 className="font-serif text-3xl lg:text-4xl text-primary leading-snug mb-4">
              写错也没关系。<br />
              <span className="text-accent italic">改对才算真正学会。</span>
            </h1>
            <p className="text-slate-500 text-base mb-10 leading-relaxed max-w-md mx-auto lg:mx-0">
              自托管账号系统已替代原来的托管登录。
              登录后，你的作文、分析结果和错题集都将保存在这台服务器上。
            </p>

            <div className="space-y-4">
              {features.map((feat, i) => (
                <div
                  key={feat.title}
                  className="flex items-start gap-4 p-4 rounded-2xl bg-white/70 backdrop-blur-sm border border-slate-100/80 shadow-sm hover:shadow-md transition-all duration-300"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <feat.icon size={18} className="text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-primary mb-0.5">{feat.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-center gap-6 justify-center lg:justify-start text-xs text-slate-400">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-emerald-500" />
                免费使用
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-emerald-500" />
                服务器本地存储
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-emerald-500" />
                数据可控
              </span>
            </div>
          </div>

          <div className="w-full max-w-[440px] animate-fade-in-up-delay-2">
            <div className="rounded-3xl bg-white shadow-2xl shadow-slate-200/50 border border-slate-100/80 overflow-hidden">
              <div className="p-8">
                <div className="flex items-center gap-2 rounded-2xl bg-slate-100 p-1 mb-6">
                  <button
                    onClick={() => {
                      setMode('signin');
                      setError(null);
                    }}
                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                      mode === 'signin' ? 'bg-white text-primary shadow-sm' : 'text-slate-500'
                    }`}
                  >
                    登录
                  </button>
                  <button
                    onClick={() => {
                      setMode('signup');
                      setError(null);
                    }}
                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                      mode === 'signup' ? 'bg-white text-primary shadow-sm' : 'text-slate-500'
                    }`}
                  >
                    注册
                  </button>
                </div>

                <div className="mb-6">
                  <h2 className="text-2xl font-serif text-primary mb-2">
                    {mode === 'signin' ? '欢迎回来' : '创建账号'}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {mode === 'signin'
                      ? '使用邮箱和密码继续你的写作练习。'
                      : inviteOnlySignup
                      ? '当前为邀请码注册模式。填写邀请码后才可创建账号。'
                      : '注册后即可把作文和分析结果保存在服务器上。'}
                  </p>
                </div>

                <div className="space-y-4">
                  {mode === 'signup' && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">邀请码</label>
                      <input
                        type="text"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                        placeholder="请输入邀请码"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all text-sm uppercase"
                      />
                      <p className="mt-2 text-xs text-slate-400">
                        {inviteOnlySignup ? '没有邀请码将无法完成注册。' : '当前服务器未强制要求邀请码。'}
                      </p>
                    </div>
                  )}

                  {mode === 'signup' && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">昵称</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="例如：Dong"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all text-sm"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">邮箱</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">密码</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="至少 8 位"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all text-sm"
                    />
                  </div>

                  {mode === 'signup' && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">确认密码</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="再次输入密码"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all text-sm"
                      />
                    </div>
                  )}
                </div>

                {error && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                    <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-white rounded-2xl font-semibold hover:bg-slate-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      提交中...
                    </>
                  ) : (
                    mode === 'signin' ? '登录并继续' : '创建账号'
                  )}
                </button>

                <p className="mt-4 text-xs text-slate-400 text-center">
                  {mode === 'signin' ? '第一次使用？切换到注册即可创建账号。' : '注册完成后会自动登录。'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
